import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import SpinWheel from "../components/SpinWheel";
import { toast } from "sonner";
import { Sparkles, ChevronLeft } from "lucide-react";

export default function AvstemningsHjul() {
  const { contestId } = useParams();
  const nav = useNavigate();
  const [wheel, setWheel] = useState(null);
  const [status, setStatus] = useState(null);   // { has_grant, spun, spin_id }
  const [spinningTo, setSpinningTo] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [animDone, setAnimDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [w, s] = await Promise.all([
          api.get("/me/vote-wheel"),
          api.get(`/me/contests/${contestId}/spin-status`),
        ]);
        setWheel(w.data.wheel);
        setStatus(s.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Klarte ikke å laste hjul");
        nav(-1);
      }
    })();
  }, [contestId, nav]);

  const doSpin = async () => {
    if (busy || !wheel) return;
    setBusy(true);
    setResult(null);
    setAnimDone(false);
    try {
      const res = await api.post(`/me/contests/${contestId}/spin`);
      const r = res.data.spin;
      setResult(r);
      setSpinningTo(r.segment_index);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Klarte ikke å spinne");
      setBusy(false);
    }
  };

  if (!wheel || !status) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-10 text-[var(--cream)]">Laster …</div>
      </Layout>
    );
  }

  const alreadySpun = status.spun;
  const noGrant = !status.has_grant;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-8 text-[var(--cream)]" data-testid="avstemnings-hjul-page">
        <Link to={`/konkurranser/${contestId}`} className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1 mb-4">
          <ChevronLeft className="h-3 w-3" /> Tilbake til konkurransen
        </Link>

        <div className="text-center mb-6">
          <Sparkles className="h-7 w-7 mx-auto mb-2 text-[var(--magenta)]" />
          <h1 className="font-display text-3xl tracking-wide">{wheel.title}</h1>
          {wheel.description && (
            <p className="text-sm text-white/60 mt-2 max-w-md mx-auto">{wheel.description}</p>
          )}
        </div>

        {noGrant && (
          <div className="bg-amber-900/30 border border-amber-500/30 p-4 text-amber-200 text-sm text-center mb-6" data-testid="vw-no-grant">
            Du har ikke låst opp et spinn for denne konkurransen. Avgi din stemme først.
          </div>
        )}

        {alreadySpun && !result && (
          <div className="bg-black/40 border border-white/10 p-4 text-white/70 text-sm text-center mb-6" data-testid="vw-already-spun">
            Du har allerede spunnet for denne konkurransen.
          </div>
        )}

        <div className="flex flex-col items-center" data-testid="vw-wheel-area">
          <SpinWheel
            segments={wheel.segments}
            spinningTo={spinningTo}
            onSpinComplete={() => setAnimDone(true)}
            size={320}
          />

          {!alreadySpun && !result && (
            <button
              data-testid="vw-spin-button"
              onClick={doSpin}
              disabled={busy || noGrant}
              className="mt-6 px-6 py-3 bg-[var(--magenta)] text-black font-meta text-sm uppercase tracking-[0.18em] hover:opacity-90 disabled:opacity-40"
            >
              {busy ? "Spinner …" : "Spinn hjulet"}
            </button>
          )}

          {result && animDone && (
            <div className="mt-6 text-center" data-testid="vw-result">
              <div className="text-xs uppercase tracking-wider text-white/50 mb-1">Du landet på</div>
              <div className="text-2xl font-display text-[var(--magenta)]">{result.segment_label}</div>
              {result.prize_kind === "chips" && result.prize_amount > 0 && (
                <div className="mt-2 text-sm text-emerald-300">+{result.prize_amount} chips er kreditert lommeboka di</div>
              )}
              {result.prize_kind === "vip_days" && result.prize_amount > 0 && (
                <div className="mt-2 text-sm text-emerald-300">+{result.prize_amount} dager VIP er lagt til kontoen din</div>
              )}
              {result.prize_kind === "none" && (
                <div className="mt-2 text-sm text-white/50">Ingen premie denne gangen — lykke til neste konkurranse!</div>
              )}
              <div className="mt-6">
                <Link to={`/konkurranser/${contestId}`} className="text-xs text-[var(--magenta)] hover:underline">
                  Tilbake til konkurransen
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
